import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Container,
    Typography,
    Paper,
    Stack,
    Chip,
    Divider,
    TextField,
    InputAdornment,
    IconButton,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    Avatar,
    Tooltip,
    Tabs,
    Tab,
    useTheme,
    useMediaQuery,
    CircularProgress,
    Alert,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import InventoryOutlinedIcon from '@mui/icons-material/InventoryOutlined';
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import QrCode2OutlinedIcon from "@mui/icons-material/QrCode2Outlined";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import CloseIcon from "@mui/icons-material/Close";

import { useAuth } from "../context/AuthContext";
import { getUserOrders } from "../services/orderService";
import { buildOrderTimeline, getOrderStatusLabelPt, normalizeOrderStatus } from "../utils/orderStatus";

// --- HELPERS & MOCKS ---

const formatBRL = (v) =>
    Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const STATUS_META = {
    "Aguardando pagamento": {
        icon: <InventoryOutlinedIcon fontSize="small" />,
        color: "warning",
        bg: "#fff7ed",
        text: "#c2410c",
    },
    Pago: {
        icon: <PaidOutlinedIcon fontSize="small" />,
        color: "info",
        bg: "#eff6ff",
        text: "#1d4ed8",
    },
    "Em separação": {
        icon: <Inventory2OutlinedIcon fontSize="small" />,
        color: "primary",
        bg: "#f0f9ff",
        text: "#0369a1",
    },
    Enviado: {
        icon: <LocalShippingOutlinedIcon fontSize="small" />,
        color: "secondary",
        bg: "#f5f3ff",
        text: "#7c3aed",
    },
    Entregue: {
        icon: <CheckCircleOutlineOutlinedIcon fontSize="small" />,
        color: "success",
        bg: "#f0fdf4",
        text: "#15803d",
    },
    Cancelado: {
        icon: <CancelOutlinedIcon fontSize="small" />,
        color: "error",
        bg: "#fef2f2",
        text: "#b91c1c",
    },
};

const pedidosSeed = [
    {
        id: "-OCPLBT-K98231XP_QA9",
        createdAt: "02/11/2025 19:18",
        status: "Entregue",
        payment: { method: "Pix", status: "Aprovado", pixCode: "00020126580014BR.GOV.BCB.PIX..." },
        shipping: {
            carrier: "Correios PAC",
            method: "PAC",
            price: 18.9,
            trackingCode: "BR123456789BR",
            trackingUrl: "https://rastreamento.correios.com.br/app/index.php",
            address: {
                cep: "36307-464",
                street: "Rua Walmir Coelho",
                number: "10, Bloco 2, apto 202",
                district: "Centro",
                city: "São João del-Rei",
                state: "MG",
            },
        },
        coupon: "WELCOME10",
        items: [
            { id: "pod-1", name: "EmmaPod Ultra", flavor: "Blueberry Ice", qty: 1, unitPrice: 119.9 },
            { id: "pod-2", name: "EmmaPod Ultra", flavor: "Grape Mint", qty: 2, unitPrice: 109.9 },
        ],
        timeline: [
            { label: "Pedido criado", at: "02/11/2025 19:18", done: true },
            { label: "Pagamento aprovado", at: "02/11/2025 19:19", done: true },
            { label: "Enviado", at: "03/11/2025 10:40", done: true },
            { label: "Entregue", at: "06/11/2025 15:12", done: true },
        ],
    },
    {
        id: "-ODJ21A-FAKE_002",
        createdAt: "14/12/2025 09:02",
        status: "Enviado",
        payment: { method: "Cartão", status: "Aprovado", nsu: "902313", auth: "A1B2C3" },
        shipping: {
            carrier: "Jadlog",
            method: "Expresso",
            price: 29.9,
            trackingCode: "JDLG987654321",
            trackingUrl: "https://www.jadlog.com.br/tracking",
            address: {
                cep: "30110-017",
                street: "Av. Afonso Pena",
                number: "1200",
                district: "Centro",
                city: "Belo Horizonte",
                state: "MG",
            },
        },
        coupon: "",
        items: [
            { id: "pod-3", name: "EmmaPod Pro", flavor: "Watermelon", qty: 1, unitPrice: 149.9 },
        ],
        timeline: [
            { label: "Pedido criado", at: "14/12/2025 09:02", done: true },
            { label: "Pagamento aprovado", at: "14/12/2025 09:03", done: true },
            { label: "Em separação", at: "14/12/2025 10:10", done: true },
            { label: "Enviado", at: "15/12/2025 18:30", done: true },
        ],
    },
    {
        id: "-ODJ21A-FAKE_003",
        createdAt: "05/01/2026 21:47",
        status: "Aguardando pagamento",
        payment: { method: "Pix", status: "Pendente", pixCode: "00020126580014BR.GOV.BCB.PIX..." },
        shipping: {
            carrier: "Correios",
            method: "SEDEX",
            price: 39.9,
            trackingCode: "",
            trackingUrl: "",
            address: {
                cep: "01001-000",
                street: "Praça da Sé",
                number: "100",
                district: "Sé",
                city: "São Paulo",
                state: "SP",
            },
        },
        coupon: "FRETEGRATIS",
        items: [
            { id: "pod-4", name: "EmmaPod Mini", flavor: "Strawberry", qty: 3, unitPrice: 69.9 },
        ],
        timeline: [{ label: "Pedido criado", at: "05/01/2026 21:47", done: true }],
    },
];

function calcSubtotal(items = []) {
    return items.reduce((acc, it) => acc + (it.unitPrice || 0) * (it.qty || 0), 0);
}

function calcDiscountFromCoupon(subtotal, coupon) {
    if (!coupon) return 0;
    if (coupon === "WELCOME10") return subtotal * 0.1;
    if (coupon === "FRETEGRATIS") return 0;
    return 0;
}

function formatDateTimeBR(ts) {
    if (!ts) return "-";
    const d = new Date(ts);
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, "0");
    const min = String(d.getMinutes()).padStart(2, "0");
    return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
}

function mapFirebaseOrderToUi(order) {
    const statusNorm = normalizeOrderStatus(order?.status);
    const statusPt = getOrderStatusLabelPt(statusNorm);

    const createdAtTs = typeof order?.createdAt === "number" ? order.createdAt : Date.now();

    const paymentMethod = order?.payment?.method === "card" ? "Cartão" : "Pix";
    const payment = {
        method: paymentMethod,
        status: statusPt === "Aguardando pagamento" ? "Pendente" : "Aprovado",
        pixCode: order?.payment?.method === "pix" ? (order?.payment?.pixCode || "") : "",
    };

    const items = (order?.items || []).map((it) => ({
        id: it.productId || it.id || "",
        name: it.productName || it.name || "",
        flavor: it.flavor || "",
        qty: Number(it.quantity ?? it.qty ?? 0),
        unitPrice: Number(it.price ?? it.unitPrice ?? 0),
        image: it.image || null,
    }));

    const shippingAddress = {
        cep: order?.address?.cep || "",
        street: order?.address?.street || "",
        number: order?.address?.number || "",
        district: order?.address?.district || "",
        city: order?.address?.city || "",
        state: order?.address?.state || "",
    };

    const timeline = buildOrderTimeline({
        status: statusNorm,
        createdAt: createdAtTs,
        events: order?.events,
    });

    return {
        id: order?.id,
        createdAt: formatDateTimeBR(createdAtTs),
        createdAtTs,
        status: statusPt,
        payment,
        shipping: {
            carrier: order?.shipping?.carrier || "",
            method: order?.shipping?.method || "",
            price: Number(order?.shipping?.price || 0),
            trackingCode: order?.shipping?.trackingCode || "",
            trackingUrl: order?.shipping?.trackingUrl || "",
            address: shippingAddress,
        },
        coupon: order?.coupon || "",
        items,
        timeline,
    };
}

// --- COMPONENTS ---

function StatusChip({ status, variant = "filled" }) {
    const meta = STATUS_META[status] || { icon: <InfoOutlinedIcon fontSize="small" />, color: "default", bg: "#eee", text: "#444" };

    if (variant === "soft") {
        return (
            <Chip
                icon={meta.icon}
                label={status}
                sx={{
                    fontWeight: 700,
                    borderRadius: 2,
                    bgcolor: meta.bg,
                    color: meta.text,
                    border: 'none',
                    "& .MuiChip-icon": { color: meta.text }
                }}
            />
        )
    }

    return (
        <Chip
            icon={meta.icon}
            label={status}
            color={meta.color}
            variant={variant}
            sx={{ fontWeight: 700, borderRadius: 2 }}
        />
    );
}

function InfoRow({ label, value, mono = false, copyable = false }) {
    const handleCopy = async () => {
        try { await navigator.clipboard.writeText(value); } catch (e) { }
    };

    return (
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} sx={{ py: 0.5 }}>
            <Typography variant="body2" sx={{ color: "var(--cinza)", fontWeight: 500 }}>{label}</Typography>
            <Stack direction="row" alignItems="center" spacing={0.5}>
                <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, textAlign: 'right' }}
                    style={mono ? { fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" } : {}}
                >
                    {value || "-"}
                </Typography>
                {copyable && value && (
                    <Tooltip title="Copiar">
                        <IconButton size="small" onClick={handleCopy} sx={{ p: 0.5 }}>
                            <ContentCopyOutlinedIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                    </Tooltip>
                )}
            </Stack>
        </Stack>
    );
}

function MetricCard({ title, value, icon, colorBg, colorText }) {
    return (
        <Paper elevation={0} sx={{
            p: 2.5,
            borderRadius: 4,
            height: '100%',
            bgcolor: colorBg,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            transition: 'transform 0.2s',
            '&:hover': { transform: 'translateY(-4px)' }
        }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box sx={{
                    p: 1,
                    borderRadius: "50%",
                    bgcolor: "rgba(255,255,255,0.6)",
                    color: colorText,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    {icon}
                </Box>
                <Typography variant="h3" sx={{ fontWeight: 800, color: colorText }}>{value}</Typography>
            </Stack>
            <Typography variant="subtitle2" sx={{ mt: 1, fontWeight: 700, color: colorText, opacity: 0.9 }}>
                {title}
            </Typography>
        </Paper>
    )
}

export default function MeusPedidos() {
    const { user } = useAuth();

    const [query, setQuery] = useState("");
    const [tab, setTab] = useState("Todos");
    const [open, setOpen] = useState(false);
    const [selected, setSelected] = useState(null);

    // Mantém o nome "pedidosMock" para reaproveitar a UI existente, mas agora vem do Firebase
    const [pedidosMock, setPedidosMock] = useState([]);
    const [loadingOrders, setLoadingOrders] = useState(true);
    const [loadError, setLoadError] = useState("");

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const tabs = ["Todos", "Pago", "Em separação", "Enviado", "Entregue", "Cancelado"];

    useEffect(() => {
        async function carregar() {
            setLoadingOrders(true);
            setLoadError("");

            // Sem usuário logado -> não carrega pedidos
            if (!user?.uid) {
                setPedidosMock([]);
                setLoadingOrders(false);
                return;
            }

            try {
                const orders = await getUserOrders(user.uid);
                const mapped = (orders || []).map(mapFirebaseOrderToUi);
                // ordena por data (mais recente primeiro)
                mapped.sort((a, b) => (b.createdAtTs || 0) - (a.createdAtTs || 0));
                setPedidosMock(mapped);
            } catch (err) {
                console.error("Erro ao carregar pedidos:", err);
                setLoadError("Não foi possível carregar seus pedidos agora.");
                // fallback de desenvolvimento (evita tela vazia durante seed local)
                setPedidosMock(pedidosSeed);
            } finally {
                setLoadingOrders(false);
            }
        }

        carregar();
    }, [user?.uid]);

    const pedidosFiltrados = useMemo(() => {
        const q = query.trim().toLowerCase();
        return pedidosMock
            .filter((p) => (tab === "Todos" ? true : p.status === tab))
            .filter((p) => {
                if (!q) return true;
                const hay = [
                    p.id, p.status, p.createdAt, p.shipping?.trackingCode, p.shipping?.carrier,
                    p.payment?.method, p.coupon,
                    ...(p.items || []).map((i) => `${i.name} ${i.flavor}`),
                ].join(" ").toLowerCase();
                return hay.includes(q);
            })
            .sort((a, b) => (b.createdAtTs || 0) - (a.createdAtTs || 0));
    }, [query, tab, pedidosMock]);

    const resumo = useMemo(() => {
        return {
            total: pedidosMock.length,
            entregues: pedidosMock.filter((p) => p.status === "Entregue").length,
            emAndamento: pedidosMock.filter((p) => ["Pago", "Em separação"].includes(p.status)).length,
            enviados: pedidosMock.filter((p) => p.status === "Enviado").length,
        };
    }, [pedidosMock]);

    const abrirDetalhes = (pedido) => {
        setSelected(pedido);
        setOpen(true);
    };

    const copiar = async (text) => {
        try { await navigator.clipboard.writeText(text); } catch (e) { console.log(e); }
    };

    if (loadingOrders) {
        return (
            <Box sx={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Stack spacing={2} alignItems="center">
                    <CircularProgress />
                    <Typography sx={{ color: "var(--cinza)" }}>Carregando seus pedidos...</Typography>
                </Stack>
            </Box>
        );
    }

    if (loadError) {
        return (
            <Box sx={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center", p: 3 }}>
                <Alert severity="error" sx={{ maxWidth: 520, width: "100%" }}>
                    {loadError}
                </Alert>
            </Box>
        );
    }

    if (!user) {
        return (
            <Box sx={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center", p: 3 }}>
                <Alert severity="warning" sx={{ maxWidth: 520, width: "100%" }}>
                    Faça login para visualizar seus pedidos.
                </Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ minHeight: "100vh", bgcolor: "#f8fafc", py: { xs: 3, md: 5 } }}>
            <Container maxWidth="lg">

                {/* HEADER SECTION */}
                <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", md: "center" }} spacing={2} mb={4}>
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: -1, color: "#1e293b" }}>
                            Meus Pedidos
                        </Typography>
                        <Typography variant="body1" sx={{ color: "var(--cinza)", mt: 0.5 }}>
                            Gerencie suas compras e acompanhe as entregas.
                        </Typography>
                    </Box>
                    <TextField
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Buscar pedido, produto..."
                        size="small"
                        InputProps={{
                            startAdornment: (<InputAdornment position="start"><SearchIcon color="action" /></InputAdornment>),
                        }}
                        sx={{
                            width: { xs: "100%", md: 300 },
                            "& .MuiOutlinedInput-root": {
                                borderRadius: 3,
                                bgcolor: "white",
                                boxShadow: "0 2px 10px rgba(0,0,0,0.03)",
                                "& fieldset": { border: "1px solid #e2e8f0" },
                                "&:hover fieldset": { borderColor: "#cbd5e1" },
                                "&.Mui-focused fieldset": { borderColor: theme.palette.primary.main }
                            }
                        }}
                    />
                </Stack>

                {/* METRICS GRID */}
                <Grid container spacing={0} sx={{ mb: 4, width: "100%",  display: "flex", flexDirection: "row", justifyContent: "space-between" }}>
                    <Grid item sx={{ width: "23%"}}>
                        <MetricCard title="Total de Pedidos" value={resumo.total} icon={<ShoppingBagOutlinedIcon />} colorBg="#eff6ff" colorText="#1d4ed8" />
                    </Grid>
                    <Grid item sx={{width: "23%"}}>
                        <MetricCard title="Em Trânsito/Separação" value={resumo.emAndamento} icon={<InventoryOutlinedIcon />} colorBg="#f5f3ff" colorText="#7c3aed" />
                    </Grid>
                    <Grid item sx={{width: "23%"}}>
                        <MetricCard title="Enviados" value={resumo.enviados} icon={<LocalShippingOutlinedIcon />} colorBg="#fff7ed" colorText="#c2410c" />
                    </Grid>
                    <Grid item sx={{width: "23%"}}>
                        <MetricCard title="Entregues" value={resumo.entregues} icon={<CheckCircleOutlineOutlinedIcon />} colorBg="#f0fdf4" colorText="#15803d" />
                    </Grid>
                </Grid>

                {/* TABS E FILTROS */}
                <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
                    <Tabs
                        value={tab}
                        onChange={(_, v) => setTab(v)}
                        variant="scrollable"
                        scrollButtons="auto"
                        allowScrollButtonsMobile
                        sx={{
                            "& .MuiTabs-indicator": { display: "none" },
                            "& .MuiTab-root": {
                                textTransform: "none",
                                fontWeight: 600,
                                fontSize: 14,
                                borderRadius: 99,
                                minHeight: 36,
                                px: 2,
                                mr: 1,
                                mb: 1,
                                color: "var(--cinza)",
                                "&.Mui-selected": {
                                    bgcolor: "var(--azul)",
                                    color: "white",
                                },

                            },
                        }}
                    >
                        {tabs.map((t) => (
                            <Tab key={t} value={t} label={t} disableRipple />
                        ))}
                    </Tabs>
                </Box>

                {/* LISTA DE PEDIDOS */}
                <Stack spacing={2.5}>
                    {pedidosFiltrados.length === 0 ? (
                        <Paper sx={{ p: 6, borderRadius: 4, textAlign: "center", border: "1px dashed #cbd5e1", bgcolor: "transparent" }} elevation={0}>
                            <Box sx={{ color: "text.disabled", mb: 2 }}><SearchIcon sx={{ fontSize: 48 }} /></Box>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: "var(--cinza)" }}>Nenhum pedido encontrado</Typography>
                            <Typography variant="body2" sx={{ color: "text.disabled" }}>Tente ajustar os filtros ou buscar por outro termo.</Typography>
                        </Paper>
                    ) : (
                        pedidosFiltrados.map((p) => {
                            const subtotal = calcSubtotal(p.items);
                            const discount = calcDiscountFromCoupon(subtotal, p.coupon);
                            const total = subtotal - discount + (p.shipping?.price || 0);

                            return (
                                <Paper
                                    key={p.id}
                                    elevation={0}
                                    sx={{
                                        p: 0,
                                        borderRadius: 3,
                                        border: "1px solid #e2e8f0",
                                        transition: "all 0.2s ease-in-out",
                                        overflow: 'hidden',
                                        "&:hover": {
                                            borderColor: "primary.light",
                                            boxShadow: "0 10px 30px -10px rgba(0,0,0,0.08)"
                                        },
                                    }}
                                >
                                    {/* CARD HEADER */}
                                    <Box sx={{ p: 2, bgcolor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                                        <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                                            <Stack direction="row" spacing={1.5} alignItems="center">
                                                <Box sx={{ bgcolor: "white", p: 0.8, borderRadius: 1.5, border: "1px solid #e2e8f0" }}>
                                                    <ShoppingBagOutlinedIcon fontSize="small" color="action" />
                                                </Box>
                                                <Box>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 800, fontFamily: "monospace" }}>
                                                        {p.id}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: "var(--cinza)", fontWeight: 500 }}>
                                                        {p.createdAt}
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                            <StatusChip status={p.status} variant="soft" />
                                        </Stack>
                                    </Box>

                                    {/* CARD BODY */}
                                    <Box sx={{ p: { xs: 2, md: 3 } }} >
                                        <Grid container spacing={3} sx={{ display: "flex", flexDirection: "row", width: "100%", justifyContent: "space-between" }}>
                                            {/* Items Preview */}
                                            <Grid item xs={12} md={7}>
                                                <Stack spacing={1.5}>
                                                    {(p.items || []).slice(0, 2).map((it) => (
                                                        <Stack key={`${it.id}-${it.flavor}`} direction="row" alignItems="center" spacing={2}>
                                                            <Avatar sx={{ bgcolor: "#eff6ff", color: "primary.main", fontWeight: 700, borderRadius: 2 }}>
                                                                {String(it.name).charAt(0)}
                                                            </Avatar>
                                                            <Box>
                                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{it.name}</Typography>
                                                                <Typography variant="caption" sx={{ color: "var(--cinza)" }}>
                                                                    {it.flavor} • x{it.qty}
                                                                </Typography>
                                                            </Box>
                                                        </Stack>
                                                    ))}
                                                    {(p.items || []).length > 2 && (
                                                        <Typography variant="caption" sx={{ pl: 7, color: "var(--cinza)", fontWeight: 600 }}>
                                                            + {(p.items || []).length - 2} outros itens...
                                                        </Typography>
                                                    )}
                                                </Stack>
                                            </Grid>

                                            {/* Actions & Price */}
                                            <Grid item xs={12} md={5}>
                                                <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "stretch", sm: "center" }} justifyContent={{ md: "flex-end" }} spacing={3}>
                                                    <Box sx={{ textAlign: { xs: "left", sm: "right" } }}>
                                                        <Typography variant="caption" sx={{ color: "var(--cinza)", display: 'block' }}>Valor Total</Typography>
                                                        <Typography variant="h5" sx={{ fontWeight: 800, color: "text.primary" }}>
                                                            {formatBRL(total)}
                                                        </Typography>
                                                    </Box>
                                                    <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />

                                                    <Stack direction="row" spacing={1}>
                                                        {p.shipping?.trackingUrl && (
                                                            <Tooltip title="Rastrear">
                                                                <IconButton
                                                                    onClick={() => window.open(p.shipping.trackingUrl, "_blank")}
                                                                    sx={{ border: "1px solid #e2e8f0" }}
                                                                >
                                                                    <LocalShippingOutlinedIcon />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                        <Button
                                                            variant="contained"
                                                            disableElevation
                                                            endIcon={<ArrowForwardIosIcon sx={{ fontSize: "12px !important" }} />}
                                                            onClick={() => abrirDetalhes(p)}
                                                            sx={{ borderRadius: 3, fontWeight: 700, px: 3, bgcolor: "#1e293b" }}
                                                        >
                                                            Detalhes
                                                        </Button>
                                                    </Stack>
                                                </Stack>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </Paper>
                            );
                        })
                    )}
                </Stack>
            </Container>

            {/* DIALOG DETALHES */}
            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                fullWidth
                maxWidth="md"
                PaperProps={{ sx: { borderRadius: 4 } }}
                fullScreen={isMobile}
                scroll="paper"
            >
                <DialogTitle sx={{
                    p: 3,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    borderBottom: "1px solid #f1f5f9"
                }}>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                        <ReceiptLongOutlinedIcon color="primary" />
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>Detalhes do Pedido</Typography>
                            <Typography variant="caption" sx={{ color: "var(--cinza)", fontFamily: "monospace" }}>
                                {selected?.id}
                            </Typography>
                        </Box>
                    </Stack>
                    <IconButton onClick={() => setOpen(false)} sx={{ bgcolor: "#f1f5f9" }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent dividers sx={{ p: 0, bgcolor: "#f8fafc" }}>
                    {!selected ? null : (
                        <Box container sx={{ display: "flex", flexDirection: "row", justifyContent: "start" }}>
                            {/* COLUNA ESQUERDA: STATUS E ITENS */}
                            <Grid item xs={12} md={8} sx={{ p: 3, borderRight: { md: "1px solid #e2e8f0" }, bgcolor: "white" }}>
                                <Stack spacing={4}>
                                    {/* Status Atual */}
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, textTransform: "uppercase", letterSpacing: 0.5, color: "var(--cinza)", fontSize: 11 }}>
                                            Status do Pedido
                                        </Typography>
                                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, borderColor: "primary.light", bgcolor: "primary.50" }}>
                                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                <Stack direction="row" spacing={2} alignItems="center">
                                                    <Avatar sx={{ bgcolor: "white", color: "primary.main" }}>
                                                        <LocalShippingOutlinedIcon />
                                                    </Avatar>
                                                    <Box>
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{selected.status}</Typography>
                                                        <Typography variant="caption" sx={{ color: "var(--cinza)" }}>
                                                            Atualizado recentemente
                                                        </Typography>
                                                    </Box>
                                                </Stack>

                                            </Stack>
                                        </Paper>
                                    </Box>

                                    {/* Itens */}
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, textTransform: "uppercase", letterSpacing: 0.5, color: "var(--cinza)", fontSize: 11 }}>
                                            Produtos
                                        </Typography>
                                        <Stack spacing={2}>
                                            {(selected.items || []).map((it) => (
                                                <Stack key={`${it.id}-${it.flavor}`} direction="row" spacing={2} alignItems="flex-start">
                                                    <Avatar variant="rounded" sx={{ width: 48, height: 48, bgcolor: "#f1f5f9", color: "var(--cinza)", fontWeight: 700 }}>
                                                        {String(it.name).charAt(0)}
                                                    </Avatar>
                                                    <Box sx={{ flex: 1 }}>
                                                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{it.name}</Typography>
                                                        <Typography variant="caption" sx={{ color: "var(--cinza)" }}>{it.flavor}</Typography>
                                                    </Box>
                                                    <Box sx={{ textAlign: "right" }}>
                                                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{formatBRL(it.unitPrice * it.qty)}</Typography>
                                                        <Typography variant="caption" sx={{ color: "var(--cinza)" }}>{it.qty}x {formatBRL(it.unitPrice)}</Typography>
                                                    </Box>
                                                </Stack>
                                            ))}
                                        </Stack>
                                    </Box>

                                    {/* Timeline Vertical Customizada */}
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, textTransform: "uppercase", letterSpacing: 0.5, color: "var(--cinza)", fontSize: 11 }}>
                                            Linha do Tempo
                                        </Typography>
                                        <Box sx={{ pl: 1 }}>
                                            {selected.timeline?.map((t, idx) => (
                                                <Box key={idx} sx={{ position: 'relative', pl: 3, pb: idx === selected.timeline.length - 1 ? 0 : 3, borderLeft: '2px solid', borderColor: t.done ? 'primary.main' : 'grey.200' }}>
                                                    <Box sx={{
                                                        position: 'absolute',
                                                        left: -6,
                                                        top: 0,
                                                        width: 10,
                                                        height: 10,
                                                        borderRadius: '50%',
                                                        bgcolor: t.done ? 'primary.main' : 'grey.300',
                                                        border: '2px solid white'
                                                    }} />
                                                    <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1 }}>{t.label}</Typography>
                                                    <Typography variant="caption" sx={{ color: "var(--cinza)" }}>{t.at}</Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    </Box>
                                </Stack>
                            </Grid>

                            {/* COLUNA DIREITA: RESUMO E ENDEREÇO */}
                            <Grid item xs={12} md={4} sx={{ p: 3 }}>
                                <Stack spacing={3} >

                                    {/* Valores */}
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, textTransform: "uppercase", letterSpacing: 0.5, color: "var(--cinza)", fontSize: 11 }}>
                                            Valores
                                        </Typography>
                                        <Stack spacing={1}>
                                            <InfoRow label="Subtotal" value={formatBRL(calcSubtotal(selected.items))} />
                                            <InfoRow label="Frete" value={formatBRL(selected.shipping?.price)} />
                                            <InfoRow label="Desconto" value={selected.coupon ? "- " + formatBRL(calcDiscountFromCoupon(calcSubtotal(selected.items), selected.coupon)) : null} />
                                            <Divider sx={{ my: 1 }} />
                                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                <Typography variant="body1" sx={{ fontWeight: 800 }}>Total</Typography>
                                                <Typography variant="body1" sx={{ fontWeight: 800, color: "primary.main" }}>
                                                    {formatBRL(
                                                        calcSubtotal(selected.items) -
                                                        calcDiscountFromCoupon(calcSubtotal(selected.items), selected.coupon) +
                                                        (selected.shipping?.price || 0)
                                                    )}
                                                </Typography>
                                            </Stack>
                                        </Stack>
                                    </Box>

                                    {/* Pagamento */}
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, textTransform: "uppercase", letterSpacing: 0.5, color: "var(--cinza)", fontSize: 11 }}>
                                            Pagamento
                                        </Typography>
                                        <InfoRow label="Método" value={selected.payment?.method} />
                                        {selected.payment?.pixCode && (
                                            <Box sx={{ mt: 1, p: 1.5, bgcolor: "grey.50", borderRadius: 2, border: "1px dashed #cbd5e1" }}>
                                                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                                                    <Stack direction="row" spacing={1} alignItems="center">
                                                        <QrCode2OutlinedIcon fontSize="small" color="action" />
                                                        <Typography variant="caption" sx={{ fontFamily: "monospace", maxWidth: 180 }} noWrap>
                                                            {selected.payment.pixCode}
                                                        </Typography>
                                                    </Stack>
                                                    <IconButton size="small" onClick={() => copiar(selected.payment.pixCode)}>
                                                        <ContentCopyOutlinedIcon fontSize="small" />
                                                    </IconButton>
                                                </Stack>
                                            </Box>
                                        )}
                                    </Box>

                                    {/* Entrega */}
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, textTransform: "uppercase", letterSpacing: 0.5, color: "var(--cinza)", fontSize: 11 }}>
                                            Endereço de Entrega
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {selected.shipping?.address?.street}, {selected.shipping?.address?.number}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: "var(--cinza)" }}>
                                            {selected.shipping?.address?.district}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: "var(--cinza)" }}>
                                            {selected.shipping?.address?.city} - {selected.shipping?.address?.state}
                                        </Typography>
                                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontFamily: "monospace" }}>
                                            CEP: {selected.shipping?.address?.cep}
                                        </Typography>
                                    </Box>

                                    {/* Rastreio */}
                                    {selected.shipping?.trackingCode && (
                                        <Box>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, textTransform: "uppercase", letterSpacing: 0.5, color: "var(--cinza)", fontSize: 11 }}>
                                                Rastreamento
                                            </Typography>
                                            <InfoRow label="Código" value={selected.shipping.trackingCode} mono copyable />
                                            <Button
                                                fullWidth
                                                variant="outlined"
                                                startIcon={<LocalShippingOutlinedIcon />}
                                                sx={{ mt: 1, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                                                onClick={() => window.open(selected.shipping.trackingUrl, "_blank")}
                                            >
                                                Rastrear Objeto
                                            </Button>
                                        </Box>
                                    )}

                                </Stack>
                            </Grid>
                        </Box>
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
}